/* eslint-disable @typescript-eslint/restrict-template-expressions */
import DOMPurify from 'dompurify';
import { observer } from 'mobx-react';
import { formatPhoneNumber } from 'react-phone-number-input';

// Components
import { CaretLeftIcon } from 'assets/custom-icons';
import { Collapse } from 'top-component-library';

// Utils
import combineURLs from 'axios/lib/helpers/combineURLs';
import { formatAddress } from 'utils/Address';

// Tracking
import { bosLandingPageTrackingEvents } from 'integrations/segment/tracking-events';
import { TODO } from 'utils/Types';
import {
    Body,
    CollapseSection,
    Container,
    Distance,
    ListItem,
    Name,
    OrderButton,
    PhoneAddress,
    StoreHoursText,
    VendorList,
    VendorLogo,
    VendorName,
} from './styles';

// Returns a distance string rounded to the nearest tenth of a mile.
const getDistance = (distance: number) => {
  const roundedDistance = Math.round(distance * 10) / 10;
  return `${parseFloat(roundedDistance.toFixed(1))} mile${roundedDistance === 1 ? '' : 's'} away`;
};

function LocationCard({ customer, location, vendors }) {
  if (!customer || !location) {
    return null;
  }
  const { distanceInMiles, phone_for_patrons, physical_address, store_hours } = customer;
  const { locationName, locationShortId } = location;

  const renderVendor = (vendor: TODO): React.ReactNode => {
    const keyName = `vendor-item-${vendor.customer_id.toString()}`;
    if (vendor.brand_parent?.logo_url) {
      return (
        <ListItem key={keyName}>
          <VendorLogo alt="" src={combineURLs(process.env.REACT_APP_STATIC_ROOT, vendor.brand_parent?.logo_url)} />
          <VendorName>{vendor.customer_name}</VendorName>
        </ListItem>
      );
    } else if (vendor.logo_url) {
      return (
        <ListItem key={keyName}>
          <VendorLogo alt="" src={combineURLs(process.env.REACT_APP_STATIC_ROOT, vendor.logo_url)} />
          <VendorName>{vendor.customer_name}</VendorName>
        </ListItem>
      );
    } else {
      return (
        <ListItem key={keyName}>
          <VendorName>{vendor.customer_name}</VendorName>
        </ListItem>
      );
    }
  };
  return (
    <Container>
      <Body>
        {/* Distance */}
        {distanceInMiles >= 0 && <Distance>{getDistance(distanceInMiles)}</Distance>}
        {/* Name */}
        <Name>{locationName}</Name>
        {/* Address */}
        {physical_address && <PhoneAddress>{formatAddress(physical_address)}</PhoneAddress>}
        {/* Phone */}
        {phone_for_patrons && <PhoneAddress>{formatPhoneNumber(phone_for_patrons)}</PhoneAddress>}
      </Body>
      {/* Store Hours */}
      {store_hours && (
        <CollapseSection
          onChange={() =>
            bosLandingPageTrackingEvents.trackClickStoreHours({ locationShortId: location.locationShortId })
          }
          expandIcon={() => <CaretLeftIcon />}
          expandIconPosition="right"
        >
          <Collapse.Panel header="Store Hours" key="store-hours">
            <StoreHoursText dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(store_hours) }} />
          </Collapse.Panel>
        </CollapseSection>
      )}
      {/* Vendors */}
      {/* TODO: Remove [window.location.hostname !== 'app.alontihub.com'] when we make showing the vendors configurable  */}
      {vendors.length > 0 && window.location.hostname !== 'app.alontihub.com' && (
        <CollapseSection expandIcon={() => <CaretLeftIcon />} expandIconPosition="right">
          <VendorList header={`${vendors.length} Vendor${vendors.length > 1 ? 's' : ''} `} key="vendor-list">
            {vendors.map((vendor: TODO) => renderVendor(vendor))}
          </VendorList>
        </CollapseSection>
      )}
      {/* Button */}
      <OrderButton to={`/${locationShortId}`}>Start Order</OrderButton>
    </Container>
  );
}

export default observer(LocationCard);
